import React from 'react';
import { DashboardFilterState } from '@/types/dashboard';

interface DashboardFiltersProps {
  filters: DashboardFilterState;
  onFilterChange: (newFilters: DashboardFilterState) => void;
  onRefresh: () => void;
  isLoading: boolean;
}

export default function DashboardFilters({
  filters,
  onFilterChange,
  onRefresh,
  isLoading,
}: DashboardFiltersProps) {
  function handleMarketplaceChange(e: React.ChangeEvent<HTMLSelectElement>) {
    onFilterChange({
      ...filters,
      marketplace: e.target.value,
    });
  }

  function handleDateFromChange(e: React.ChangeEvent<HTMLInputElement>) {
    onFilterChange({
      ...filters,
      dateFrom: e.target.value || undefined,
    });
  }

  function handleDateToChange(e: React.ChangeEvent<HTMLInputElement>) {
    onFilterChange({
      ...filters,
      dateTo: e.target.value || undefined,
    });
  }

  function applyPreset(preset: 'today' | '7d' | '30d' | 'mtd') {
    const today = new Date();
    const toStr = today.toISOString().split('T')[0];
    let fromStr = '';

    if (preset === 'today') {
      fromStr = toStr;
    } else if (preset === '7d') {
      const past = new Date(today);
      past.setDate(past.getDate() - 6);
      fromStr = past.toISOString().split('T')[0];
    } else if (preset === '30d') {
      const past = new Date(today);
      past.setDate(past.getDate() - 29);
      fromStr = past.toISOString().split('T')[0];
    } else if (preset === 'mtd') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      fromStr = firstDay.toISOString().split('T')[0];
    }

    onFilterChange({
      ...filters,
      dateFrom: fromStr,
      dateTo: toStr,
    });
  }

  // Determine active preset if dates match
  const todayStr = new Date().toISOString().split('T')[0];
  const firstDayStr = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
  
  const isToday = filters.dateFrom === todayStr && filters.dateTo === todayStr;
  const isMtd = filters.dateFrom === firstDayStr && filters.dateTo === todayStr;

  return (
    <div className="rounded-xl bg-white p-4 shadow-xs border border-gray-200/80 flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Marketplace Selection */}
        <div className="relative">
          <label htmlFor="marketplace-filter" className="sr-only">
            Marketplace Channel
          </label>
          <div className="flex items-center">
            <select
              id="marketplace-filter"
              value={filters.marketplace || 'ALL'}
              onChange={handleMarketplaceChange}
              className="appearance-none rounded-lg border border-gray-200 bg-gray-50/50 pl-8 pr-8 py-1.5 text-xs font-semibold text-gray-800 shadow-xs hover:bg-gray-100/50 focus:border-indigo-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 cursor-pointer transition-all"
            >
              <option value="ALL">🌐 All Marketplaces</option>
              <option value="FLIPKART">🟡 Flipkart India</option>
              <option value="AMAZON">🟠 Amazon IN</option>
            </select>
            <span className="pointer-events-none absolute left-2.5 text-xs">
              {filters.marketplace === 'FLIPKART' ? '🟡' : filters.marketplace === 'AMAZON' ? '🟠' : '🌐'}
            </span>
            <span className="pointer-events-none absolute right-2.5 text-gray-400 text-[10px]">
              ▼
            </span>
          </div>
        </div>

        {/* Date Range Inputs */}
        <div className="flex items-center gap-1.5 bg-gray-50/70 border border-gray-200 rounded-lg p-1">
          <input
            type="date"
            aria-label="Start date"
            value={filters.dateFrom || ''}
            onChange={handleDateFromChange}
            className="rounded-md border-0 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-xs focus:ring-1 focus:ring-indigo-500 focus:outline-hidden"
          />
          <span className="text-xs text-gray-400 font-medium px-1">➔</span>
          <input
            type="date"
            aria-label="End date"
            value={filters.dateTo || ''}
            onChange={handleDateToChange}
            className="rounded-md border-0 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-xs focus:ring-1 focus:ring-indigo-500 focus:outline-hidden"
          />
        </div>

        {/* Quick Presets */}
        <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
          <button
            type="button"
            onClick={() => applyPreset('today')}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
              isToday
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => applyPreset('7d')}
            className="rounded-md px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all"
          >
            7 Days
          </button>
          <button
            type="button"
            onClick={() => applyPreset('30d')}
            className="rounded-md px-2.5 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all"
          >
            30 Days
          </button>
          <button
            type="button"
            onClick={() => applyPreset('mtd')}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
              isMtd
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            MTD
          </button>
        </div>
      </div>

      {/* Refresh Action */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white px-3.5 py-1.5 text-xs font-bold text-gray-700 border border-gray-200 shadow-xs hover:bg-gray-50 active:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-50 transition-all"
        >
          <svg
            className={`h-3.5 w-3.5 text-gray-500 ${isLoading ? 'animate-spin text-indigo-600' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span>{isLoading ? 'Syncing...' : 'Sync Live'}</span>
        </button>
      </div>
    </div>
  );
}

